import * as React from "react";

/**
 * useIsLargeScreen — MS582 helper hook for desktop-monitor layout
 * branches.
 *
 * Returns true when the viewport is at least `LARGE_SCREEN_BREAKPOINT`
 * pixels wide (1920px, matching Tailwind's `3xl:` token introduced in
 * MS581). Used to switch the DashboardConciergeDrawer between its
 * overlay-Sheet mode (default, intimate, works on laptops) and its
 * inline-column push mode (28" Acer + larger; workbench pattern).
 *
 * Mirrors src/hooks/use-mobile.tsx for the small-screen flip — the
 * shape (state + matchMedia listener) is intentionally identical so
 * the next dev who hits this codebase recognises the pattern.
 *
 * SSR safety: returns `false` on the initial render (server +
 * jsdom) because `window` isn't guaranteed to exist. The effect
 * then re-syncs once the component mounts on the client, which is
 * the same lifecycle pattern the canonical Tailwind / shadcn
 * `use-mobile` examples follow. Consumers that need a "definitely
 * resolved" value should wait one tick before reading, but for
 * layout flips the brief "render as overlay, then snap to push on
 * the next frame" is acceptable — both layouts are valid intermediate
 * states.
 *
 * MS582 intentionally locks this hook to the 1920px threshold rather
 * than parameterising it. We don't yet have a second consumer that
 * wants a different breakpoint, and YAGNI on the abstraction; if a
 * future caller needs a custom min-width, this hook can be widened
 * then. See discovery brief §3 for the breakpoint rationale.
 */

const LARGE_SCREEN_BREAKPOINT = 1920;

export function useIsLargeScreen() {
  const [isLarge, setIsLarge] = React.useState<boolean>(() => {
    // SSR / jsdom guard. `typeof window` is the canonical guard
    // here — `window` itself can be undefined on the server, and
    // checking for the global by name avoids triggering a
    // ReferenceError before the first render commits.
    if (typeof window === "undefined") return false;
    return window.innerWidth >= LARGE_SCREEN_BREAKPOINT;
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const mql = window.matchMedia(`(min-width: ${LARGE_SCREEN_BREAKPOINT}px)`);
    const onChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsLarge(event.matches);
    };
    // Sync immediately in case the viewport already matches at mount
    // time. Without this, a fresh mount at 2560px would briefly read
    // `isLarge=false` from the initial useState before the listener
    // ever fires.
    onChange(mql);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isLarge;
}

export const LARGE_SCREEN_BREAKPOINT_PX = LARGE_SCREEN_BREAKPOINT;
